/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';

import Button from '@/components/Button';

import { Controller, Resolver, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { TextField } from '@/components/InputField';
import { InputFieldFile } from '@/components/InputFieldFile';
import { yupResolver } from '@hookform/resolvers/yup';
import { Dialog, Actions, HelpForm, FormInput, ErrorText } from './styles';
import {
  HelpCenterFormData,
  useHelpCenterContext,
} from '../../contexts/HelpCenterContext';

interface InputFormProps
  extends React.InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement> {
  type: string;
  name: 'mensagem' | 'nome' | 'email' | 'anexos';
  title: string;
}

const HelpCenterModal: React.FC = () => {
  const {
    state: { isModalOpen, user, isSendingEmail, error },
    actions: { dispatchCloseModal, dispatchContactEmail },
  } = useHelpCenterContext();

  const contatoEmail = React.useMemo(() => {
    if (user && user.empresa) {
      return user.empresa.email_contato;
    }

    return null;
  }, [user]);

  const schema = yup
    .object({
      nome: yup.string().required('O nome é obrigatório.'),
      email: yup
        .string()
        .email('Informe um e-mail válido.')
        .required('O email é obrigatório.'),
      mensagem: yup.string().required('A mensagem é obrigatória.'),
      anexos: yup
        .array()
        .required()
        .max(5, 'Você pode adicionar até 5 arquivos')
        .test(
          'is-accepted-file',
          'O arquivo deve ser uma imagem (jpg, png, gif, webp) ou PDF',
          value => {
            const files = value as File[];
            const allowedTypes = [
              'image/jpeg',
              'image/png',
              'image/gif',
              'image/webp',
              'application/pdf',
            ];

            return files.every(file => allowedTypes.includes(file.type));
          },
        ),
    })
    .required();

  const {
    reset,
    control,
    setFocus,
    getValues,
    handleSubmit: formHandleSubmit,
    formState: { isValid, errors },
  } = useForm<HelpCenterFormData>({
    defaultValues: {
      nome: '',
      email: '',
      mensagem: '',
      anexos: [],
    },
    mode: 'onBlur',
    shouldUnregister: false,
    resolver: yupResolver(schema) as Resolver<HelpCenterFormData>,
  });

  const handleSubmit = React.useCallback(
    async (data: HelpCenterFormData) => {
      dispatchContactEmail(data, contatoEmail || undefined);
    },
    [dispatchContactEmail, contatoEmail],
  );

  const onClose = React.useCallback(() => {
    reset();
    dispatchCloseModal();
  }, [dispatchCloseModal, reset]);

  const onAfterOpen = React.useCallback(() => {
    reset({
      nome: user?.name || '',
      email: user?.email || '',
      mensagem: '',
      anexos: [],
    });

    const values = getValues();
    const filteredObj = Object.fromEntries(
      Object.entries(values).filter(([, value]) => !value),
    );
    const firstBlank = Object.keys(filteredObj)[0] as keyof typeof values;

    setFocus(firstBlank);
  }, [getValues, reset, setFocus, user]);

  const InputForm: React.FC<InputFormProps> = ({
    type,
    name,
    title,
    ...inputProps
  }) => (
    <Controller
      name={name}
      control={control}
      render={({ field: { value, onChange, ref, ...rest } }) => {
        switch (type) {
          case 'textarea':
            return (
              <TextField
                ref={ref}
                title={title}
                error={errors[name]?.message}
                value={value as string}
                onChange={onChange}
                {...rest}
                {...inputProps}
              />
            );
          case 'file':
            return (
              <InputFieldFile
                ref={ref}
                title={title}
                error={errors.anexos?.message}
                valueList={value as File[]}
                onChangeFiles={onChange}
                {...rest}
                {...inputProps}
              />
            );
          default:
            return (
              <FormInput
                ref={ref}
                type={type}
                title={title}
                error={errors[name]?.message}
                value={value as string}
                onChange={onChange}
                {...rest}
                {...inputProps}
              />
            );
        }
      }}
    />
  );

  return (
    <Dialog
      title="Deixe uma mensagem"
      onAfterOpen={onAfterOpen}
      onAfterClose={onClose}
      isOpen={isModalOpen}
    >
      <HelpForm>
        <InputForm type="text" name="nome" title="Nome" />
        <InputForm type="email" name="email" title="E-mail" />
        <InputForm type="textarea" name="mensagem" title="Mensagem" />
        <InputForm type="file" name="anexos" title="Anexos" multiple />
        {error && <ErrorText>{error}</ErrorText>}
      </HelpForm>

      <Actions>
        <Button theme="secondary" onClick={onClose}>
          Cancelar
        </Button>
        <Button
          theme="primary"
          disabled={!isValid}
          loading={isSendingEmail}
          onClick={formHandleSubmit(handleSubmit)}
        >
          Enviar
        </Button>
      </Actions>
    </Dialog>
  );
};

export default HelpCenterModal;
